/* Click Fencing - gate placement for blade, flat top and PerfMesh.
 *
 * REACHABILITY (measured 2026-08-31, contradicts the older project notes):
 * window.glassCalculator on the blade / flat top / PerfMesh pages is NOT an inert shell -
 * it IS the live, DOM-bound calculator for that page. All four calculators are the same
 * GlassFencingCalculator class, so these are plain instance/prototype fixes.
 *
 * 1. MOVE BUTTONS SKIPPED SLOTS. applyGateStep() steps OVER the filler (offcut) slot, and
 *    gatePosition starts at the sentinel 9999. On a 9000mm flat top run with panels
 *    [2400,2400,2400,530] the filler is LAST in the cut plan, so from the middle one click
 *    landed on the far end and further clicks did nothing - slots 0 and 3 unreachable. On
 *    Pete's 7300mm run only three slots existed, so one click crossed the whole fence.
 *    The three buttons are now owned in the CAPTURE phase on document (which beats the
 *    calculator's own listeners without unbinding them) and step one slot at a time.
 *
 * 2. MIDDLE positioned by ITEM INDEX - 230mm along Pete's 7300mm run. Now targets the
 *    section midpoint.
 *
 * 3. EXACT GATE LOCATION added to flat top and PerfMesh (glass and blade ship their own,
 *    and this never duplicates theirs). It does NOT use section.gateOffsetMm: that field
 *    is honoured by the renderer but is broken - setting 300 on a 9000mm run left the gate
 *    put, shrank a 2400 panel to 1900 and drew an 8700mm fence in a 9000mm opening.
 *
 * 4. FLAT TOP DREW 50mm OVER the opening with a gate. At 7300mm with 3000mm panels:
 *      gate off -> 50+1100+50+3000+50+3000+50        = 7300 correct
 *      gate on  -> 50+130+50+3000+50+970+50+3000+50  = 7350 over
 *    optimizedConfigs carried deductions = gate + FOUR posts when the drawing needs five.
 *    A panel is now trimmed so panels + gate + posts equal the section length.
 *
 *    THIS ONLY EVER SHRINKS. A drawing LONGER than the opening will not fit and is always
 *    safe to trim, because every aluminium/mesh panel is a cut from a stocked sheet. A
 *    drawing SHORTER than the opening is deliberately left alone: the only way to close it
 *    by widening is to make a panel wider than the sheet it is cut from. Measured - an
 *    earlier version of this file padded blade 7450 (plain) from 7400 to 7450 by growing a
 *    panel to 2450mm, which cannot be cut from a 2400 sheet. Blade's short-by-50 family
 *    (L congruent to 5000 mod 2450: 5000, 7450, 9900, 12350) is inherent to Cut Panel mode
 *    with <=2400 panels; Even Panels fills those exactly (7450 = 4 x 1800, verified), and
 *    Pete's call 2026-08-31 is that this is fine and needs no fix.
 *
 *    WHICH panel absorbs a trim matters: taking it off the smallest blindly can leave a
 *    sliver, so it goes to the smallest panel that stays at or above MIN_PANEL afterwards.
 *    If nothing can absorb it the config is left alone.
 *
 * WHY SLOT OFFSETS ARE MEASURED, NOT PREDICTED: at blade 1500mm and 1800mm the renderer
 * splits a full sheet into a 500mm hinge follower beside the gate, so the drawn widths
 * differ from optimizedConfigs.panelWidths and the split MOVES with the gate. Measured on
 * blade 1500 / 9000: config [1825,1825,1825,1825,430] but slot 4 drew at 7050 where a static
 * model says 7550 - 500mm out. perfomesh.panels carries the config widths too, so nothing
 * static can predict it. Middle and Exact therefore apply a candidate and READ the drawing.
 *
 * BUT THE PREVIEW IS SHARED. There is one #section-preview for the whole job, and on a
 * multi-section fence it shows whichever section painted last - measured: asking section 2
 * (9000mm) for a gate at 4000mm reported 1380mm, which is section 1's slot 1. So a reading
 * is only trusted when the drawn total equals THAT section's length; otherwise the static
 * model is used. Single-section jobs always measure; multi-section falls back safely.
 *
 * Glass is deliberately untouched: its Move buttons already step correctly, it has an exact
 * control, and it uses variable gaps and hinge panels rather than fixed 50mm posts.
 *
 * Shape follows cf-calc-fixes.js: IIFE, prototype flag, polled install with a try limit,
 * every wrapper in try/catch so a display fix can never break the calculator.
 */
(function () {
  'use strict';

  if (!/(blade-fence-calculator|flat-top-fence-calculator|perfmesh-fence)/.test(location.pathname)) return;

  var TRIES = 0, MAX_TRIES = 200, POST = 50, MIN_PANEL = 100, MAX_SLIP = 200;

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }
  function cfg(n) { var i = inst(); return (i && i.state && i.state.optimizedConfigs) ? i.state.optimizedConfigs[n] : null; }
  function sect(n) { var i = inst(); return (i && i.state && i.state.sections) ? i.state.sections[n] : null; }
  function widths(n) { var c = cfg(n); return (c && Array.isArray(c.panelWidths) && c.panelWidths.length) ? c.panelWidths : null; }
  function maxPos(n) { var w = widths(n); return w ? w.length : 0; }
  function gateWidth(n) { var s = sect(n); return s ? (parseInt(s.gateLength, 10) || 0) : 0; }
  function sectionLength(n) { var s = sect(n), c = cfg(n); return (s && parseInt(s.length, 10)) || (c && c.originalSectionLength) || 0; }

  function offsets(n) {
    var w = widths(n); if (!w) return null;
    var out = [], acc = POST;
    for (var k = 0; k <= w.length; k++) { out.push(acc); if (k < w.length) acc += w[k] + POST; }
    return out;
  }

  // Parse the shared preview: returns { off, sum } for the gate, or null.
  function readPreview() {
    try {
      var p = document.getElementById('section-preview'); if (!p) return null;
      var raw = p.querySelectorAll('text'), nodes = [], i, t;
      for (i = 0; i < raw.length; i++) { t = raw[i].textContent.replace(/\s+/g, ' ').trim(); if (t) nodes.push(t); }
      var nums = [], types = [], hadMM = [], gi = -1, m;
      for (i = 0; i < nodes.length; i++) {
        t = nodes[i];
        m = t.match(/^(\d+)\s*(mm)?\s*(Post|Panel|Gate|Cut|Rake[d]?)?$/i);
        if (m) {
          nums.push(Number(m[1])); types.push((m[3] || '').toLowerCase()); hadMM.push(!!m[2]);
          if (m[3] && /gate/i.test(m[3])) gi = nums.length - 1;
          continue;
        }
        if (/^(Post|Panel|Gate|Cut|Rake[d]?)$/i.test(t) && nums.length) {
          types[nums.length - 1] = t.toLowerCase();
          if (/gate/i.test(t)) gi = nums.length - 1;
        }
      }
      if (nums.length > 1 && hadMM[0] && !types[0]) { nums.shift(); types.shift(); if (gi >= 0) gi--; }
      if (gi < 0) return null;
      var off = 0, sum = 0;
      for (i = 0; i < nums.length; i++) { if (i < gi) off += nums[i]; sum += nums[i]; }
      return { off: off, sum: sum };
    } catch (e) { return null; }
  }

  // Only trust the shared preview when it is showing THIS section - see the note above.
  function drawnOffsetFor(n) {
    var r = readPreview();
    if (!r) return null;
    var L = sectionLength(n);
    if (!L || r.sum !== L) return null;
    return r.off;
  }

  function curPos(n) {
    var s = sect(n); if (!s) return 0;
    var p = s.gatePosition;
    if (p === undefined || p === null || p >= 9999 || s._placeAtMiddle) p = Math.floor(maxPos(n) / 2);
    return Math.max(0, Math.min(p, maxPos(n)));
  }

  function applyPos(n, pos) {
    var fc = inst(), s = sect(n);
    if (!fc || !s) return null;
    s.gatePosition = Math.max(0, Math.min(pos, maxPos(n)));
    s.gateOffsetMm = 0;
    s._placeAtMiddle = false;
    s.gateMoved = true;
    try { delete fc.state.optimizedConfigs[n]; } catch (e) {}
    try { fc.updateCalculationResults(n); } catch (e) {}
    try { fc.renderSectionPreview(n); } catch (e) {}
    try { if (fc.trigger3DUpdate) fc.trigger3DUpdate(); } catch (e) {}
    return s.gatePosition;
  }

  function snapTo(n, target) {
    var offs = offsets(n) || [], mx = maxPos(n), best = 0, bd = Infinity, k, d;
    for (k = 0; k < offs.length; k++) { d = Math.abs(offs[k] - target); if (d < bd) { bd = d; best = k; } }
    applyPos(n, best);
    var bestOff = drawnOffsetFor(n);
    if (bestOff === null) return { slot: best, offset: offs[best], measured: false };
    var bestD = Math.abs(bestOff - target);
    [best - 1, best + 1].forEach(function (cand) {
      if (cand < 0 || cand > mx) return;
      applyPos(n, cand);
      var o = drawnOffsetFor(n);
      if (o !== null && Math.abs(o - target) < bestD) { best = cand; bestD = Math.abs(o - target); bestOff = o; }
    });
    applyPos(n, best);
    var settled = drawnOffsetFor(n);
    return { slot: best, offset: settled === null ? bestOff : settled, measured: true };
  }

  function absorberIndex(w, delta) {
    var order = [], i;
    for (i = 0; i < w.length; i++) order.push(i);
    order.sort(function (a, b) { return w[a] - w[b]; });
    for (i = 0; i < order.length; i++) if (w[order[i]] + delta >= MIN_PANEL) return order[i];
    return -1;
  }

  function fixFit(fc, n) {
    try {
      var s = fc.state.sections[n], c = fc.state.optimizedConfigs && fc.state.optimizedConfigs[n];
      if (!s || !c || !c.valid || !Array.isArray(c.panelWidths) || !c.panelWidths.length) return;
      if (c.__cfFit) return;
      var L = parseInt(s.length, 10) || c.originalSectionLength;
      if (!L) return;
      var gw = s.hasGate ? (parseInt(s.gateLength, 10) || 0) : 0;
      var comps = c.panelWidths.length + (s.hasGate ? 1 : 0);
      var total = c.panelWidths.reduce(function (a, b) { return a + b; }, 0) + gw + POST * (comps + 1);
      var delta = L - total;
      if (delta === 0) { c.__cfFit = true; return; }
      if (delta > 0) { c.__cfFit = true; return; }        // never pad - see note 4
      if (Math.abs(delta) > MAX_SLIP) return;
      var fi = absorberIndex(c.panelWidths, delta);
      if (fi < 0) return;
      var oldW = c.panelWidths[fi], nw = oldW + delta;
      c.panelWidths[fi] = nw;
      try { c.panel_space = c.panelWidths.reduce(function (a, b) { return a + b; }, 0); } catch (e) {}
      if (c.perfomesh && Array.isArray(c.perfomesh.panels)) {
        for (var i = 0; i < c.perfomesh.panels.length; i++) {
          var pm = c.perfomesh.panels[i];
          if (pm && (pm.sheet === oldW || pm.footprint === oldW)) {
            if (pm.sheet === oldW) pm.sheet = nw;
            if (pm.footprint === oldW) pm.footprint = nw;
            break;
          }
        }
      }
      c.__cfFit = true;
    } catch (e) {}
  }

  function fixAll(fc) {
    try { Object.keys((fc.state && fc.state.sections) || {}).forEach(function (n) { fixFit(fc, n); }); } catch (e) {}
  }

  function exactApply(n) {
    var note = document.getElementById('cf-gate-exact-note-' + n),
        inp  = document.getElementById('cf-gate-exact-input-' + n),
        side = document.getElementById('cf-gate-exact-side-' + n);
    var L = sectionLength(n), gw = gateWidth(n);
    var want = parseInt(inp && inp.value, 10);
    if (!L || !widths(n)) { if (note) note.textContent = 'Set a section length first.'; return; }
    if (isNaN(want)) { if (note) note.textContent = 'Enter a distance in mm.'; return; }
    var target = (side && side.value === 'right') ? (L - want - gw) : want;
    var res = snapTo(n, target);
    var fromRight = L - res.offset - gw;
    if (note) {
      note.textContent = 'Gate placed ' + res.offset + 'mm from the left (' + fromRight + 'mm from the right).' +
        (Math.abs(res.offset - target) > 1 ? ' Nearest buildable position to ' + Math.max(0, target) + 'mm.' : '');
    }
  }

  function addExact(n) {
    if (document.getElementById('section-' + n + '-gate-exact-apply')) return;
    if (document.getElementById('cf-gate-exact-' + n)) return;
    var moveBtn = document.getElementById('section-' + n + '-gate-move-right');
    if (!moveBtn || !moveBtn.parentElement) return;
    var row = moveBtn.parentElement;

    var wrap = document.createElement('div');
    wrap.id = 'cf-gate-exact-' + n;
    wrap.setAttribute('style', 'margin-top:10px;');

    var lab = document.createElement('div');
    lab.setAttribute('style', 'font-weight:600;margin-bottom:4px;font-size:14px;');
    lab.textContent = 'Exact Gate Location (mm to nearest gate edge)';
    wrap.appendChild(lab);

    var line = document.createElement('div');
    line.setAttribute('style', 'display:flex;gap:8px;align-items:center;flex-wrap:wrap;');

    var inp = document.createElement('input');
    inp.type = 'number'; inp.id = 'cf-gate-exact-input-' + n; inp.min = '0'; inp.step = '10';
    inp.placeholder = 'e.g. 3000';
    inp.setAttribute('style', 'flex:1;min-width:110px;padding:8px;border:1px solid #cbd5e1;border-radius:6px;font-size:16px;');

    var sel = document.createElement('select');
    sel.id = 'cf-gate-exact-side-' + n;
    sel.setAttribute('style', 'padding:8px;border:1px solid #cbd5e1;border-radius:6px;font-size:16px;');
    ['left', 'right'].forEach(function (v) {
      var o = document.createElement('option'); o.value = v; o.textContent = 'from ' + v; sel.appendChild(o);
    });

    var btn = document.createElement('button');
    btn.type = 'button'; btn.id = 'cf-gate-exact-apply-' + n; btn.textContent = 'Apply';
    btn.setAttribute('style', 'padding:8px 14px;background:#0e9ed5;color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;');
    btn.addEventListener('click', function () { try { exactApply(n); } catch (e) {} });
    inp.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); try { exactApply(n); } catch (e2) {} }
    });

    line.appendChild(inp); line.appendChild(sel); line.appendChild(btn);
    wrap.appendChild(line);

    var note = document.createElement('div');
    note.id = 'cf-gate-exact-note-' + n;
    note.setAttribute('style', 'margin-top:6px;font-size:13px;color:#475569;');
    wrap.appendChild(note);

    row.parentElement.insertBefore(wrap, row.nextSibling);
  }

  function sweepExact() {
    try {
      var btns = document.querySelectorAll('[id^="section-"][id$="-gate-move-right"]');
      for (var i = 0; i < btns.length; i++) {
        var m = btns[i].id.match(/^section-(\d+)-gate-move-right$/);
        if (m) addExact(m[1]);
      }
    } catch (e) {}
  }

  function install() {
    var fc = inst();
    if (!fc || !fc.state) return false;
    var P = Object.getPrototypeOf(fc);
    if (!P || P.__cfGateControls) { sweepExact(); return true; }

    document.addEventListener('click', function (e) {
      try {
        var t = e.target && e.target.closest ? e.target.closest('button') : null;
        if (!t || !t.id) return;
        var m = t.id.match(/^section-(\d+)-gate-move-(left|right|middle)$/);
        if (!m) return;
        var n = m[1], dir = m[2], s = sect(n);
        if (!s || !s.hasGate) return;
        e.stopImmediatePropagation(); e.preventDefault();
        if (dir === 'middle') {
          var L = sectionLength(n), gw = gateWidth(n);
          if (L) snapTo(n, (L / 2) - (gw / 2)); else applyPos(n, Math.floor(maxPos(n) / 2));
          return;
        }
        var cur = curPos(n);
        applyPos(n, dir === 'left' ? Math.max(0, cur - 1) : Math.min(maxPos(n), cur + 1));
      } catch (err) {}
    }, true);

    ['updateCalculationResults'].forEach(function (mth) {
      if (typeof P[mth] !== 'function') return;
      var orig = P[mth];
      P[mth] = function (n) { var r = orig.apply(this, arguments); try { fixFit(this, n); } catch (e) {} return r; };
    });
    ['renderSectionPreview'].forEach(function (mth) {
      if (typeof P[mth] !== 'function') return;
      var orig = P[mth];
      P[mth] = function (n) { try { fixFit(this, n); } catch (e) {} return orig.apply(this, arguments); };
    });
    ['updateQuoteBreakdown', 'updateQuoteSummary', 'updateComponentsQuote'].forEach(function (mth) {
      if (typeof P[mth] !== 'function') return;
      var orig = P[mth];
      P[mth] = function () { try { fixAll(this); } catch (e) {} return orig.apply(this, arguments); };
    });

    try {
      var root = document.querySelector('.calculator-content') || document.body;
      if (window.MutationObserver) {
        var tm = null;
        new MutationObserver(function () { if (tm) clearTimeout(tm); tm = setTimeout(sweepExact, 150); })
          .observe(root, { childList: true, subtree: true });
      }
    } catch (e) {}

    P.__cfGateControls = true;
    sweepExact();
    try { console.log('[cf-gate-controls] installed'); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {}
    if (done) return;
    if (++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInstall);
  } else {
    tryInstall();
  }
})();
